// ---------------------------------------------------------------------------
// Stale deployment recovery — detect and recover from post-deploy asset errors
// ---------------------------------------------------------------------------
// After a deploy, hashed chunk URLs change. PWAs and cached browser tabs may
// still reference deleted chunks. When the server returns HTML (SPA fallback)
// instead of JS/CSS, browsers throw a variety of errors depending on vendor.
//
// Rather than matching individual error strings, we match *categories*:
//
//   1. Module/chunk load failures  — the import() itself fails
//   2. MIME type mismatches        — server returned HTML instead of JS/CSS
//   3. HTML-parsed-as-JS           — browser tried to execute "<!DOCTYPE html>"
//   4. CSS preload failures        — Vite's <link rel="modulepreload"> for deleted CSS
//   5. Script network failures     — Safari's terse "Load failed" on script fetches
// ---------------------------------------------------------------------------

const STALE_DEPLOY_PATTERNS: RegExp[] = [
  // Category 1 — Module/chunk import failures (all browsers)
  /dynamically imported module/i,
  /loading chunk/i,
  /importing a module script/i,

  // Category 2 — MIME type mismatch (HTML served for JS/CSS request)
  /MIME type.*(?:javascript|css)/i,
  /not a valid JavaScript MIME/i,

  // Category 3 — HTML parsed as JavaScript (<!DOCTYPE or <html> as JS)
  /unexpected token\s+['"<]/i, // Chrome + Safari
  /expected expression, got '<'/i, // Firefox

  // Category 4 — Vite CSS preload failure
  /unable to preload CSS/i,

  // Category 5 — Generic script load failure (Safari is terse as fuck)
  /^Load failed$/i,
];

const RELOAD_KEY = "systemos:chunk-reload";
const RELOAD_COOLDOWN_MS = 10_000;

function buildCacheBustedUrl(now: number): string {
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", String(now));
  return url.toString();
}

export function isStaleChunkError(error: unknown): boolean {
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return STALE_DEPLOY_PATTERNS.some((re) => re.test(msg));
}

export async function hardReloadApp(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // Best effort
  }

  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // Best effort
  }

  const now = Date.now();
  window.location.replace(buildCacheBustedUrl(now));
}

export function handleStaleChunk(options?: { force?: boolean }): boolean {
  const now = Date.now();
  const force = options?.force ?? false;

  if (!force) {
    const lastReload = sessionStorage.getItem(RELOAD_KEY);

    // If we reloaded within the last 10 seconds, don't loop — let it crash visibly
    if (lastReload && now - Number(lastReload) < RELOAD_COOLDOWN_MS) {
      return false;
    }
  }

  sessionStorage.setItem(RELOAD_KEY, String(now));
  void hardReloadApp();
  return true;
}

export { RELOAD_KEY };
